import React from "react";
import { motion } from "framer-motion";

export default function AboutPage() {
  const services = [
    {
      title: "Expert Consultation",
      desc: "Get professional guidance tailored to your needs. Our experts provide clear, reliable, and actionable advice for your health and wellness journey.",
      icon: "💬",
    },
    {
      title: "Advanced Diagnostics",
      desc: "We use cutting-edge technology and personalized approaches to identify root causes quickly and accurately.",
      icon: "🔬",
    },
    {
      title: "Holistic Treatment",
      desc: "Our care combines science and compassion, ensuring both your mind and body are supported in every step.",
      icon: "🌿",
    },
    {
      title: "24/7 Support",
      desc: "Our dedicated support team is always available to assist with queries and provide aftercare guidance.",
      icon: "🕒",
    },
  ];

  const treatments = [
    {
      name: "Bodily Stiffness & Inflexibility",
      details: "Neck and back stiffness, frozen shoulder, carpal tunnel syndrome, joint inflexibility.",
      icon: "🧘‍♂️",
    },
    {
      name: "Muscle Soreness & Sensitivity",
      details: "Physical stress, work-related fatigue and weakness, delayed muscle onset soreness.",
      icon: "💪",
    },
    {
      name: "Joint Pain & Arthritis",
      details: "Knee pain, rheumatoid arthritis, osteoarthritis, chronic joint discomfort.",
      icon: "🦵",
    },
    {
      name: "Nerve & Spine Conditions",
      details: "Sciatica, slipped disc, cervical pain, lower back pain.",
      icon: "🧠",
    },
    {
      name: "Posture & Mobility Issues",
      details: "Work-related poor posture, muscle imbalance, mobility restriction.",
      icon: "🏃‍♀️",
    },
    {
      name: "Stress & Fatigue Disorders",
      details: "Mental burnout, chronic fatigue syndrome, work-life exhaustion.",
      icon: "😌",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-cyan-50 text-slate-800 overflow-hidden relative">
      {/* HERO SECTION */}
      <header className="relative py-20 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 0.2, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute w-[600px] h-[600px] bg-gradient-to-tr from-indigo-400 to-cyan-400 rounded-full blur-3xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        ></motion.div>

        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-extrabold text-slate-800 z-10 relative"
        >
          About <span className="text-indigo-600">ProcestaPH</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-2xl mx-auto mt-6 text-slate-600 text-lg"
        >
          We are dedicated to transforming healthcare with empathy, innovation, and expertise. Our goal is to make quality health solutions accessible, personalized, and effective for everyone.
        </motion.p>

        <motion.svg
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          viewBox="0 0 1200 400"
          className="mx-auto mt-12 w-full max-w-4xl"
        >
          <defs>
            <linearGradient id="aboutGrad" x1="0" x2="1">
              <stop offset="0%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
          </defs>

          <motion.path
            d="M100,200 C200,100 400,300 600,200 C800,100 1000,300 1100,200"
            stroke="url(#aboutGrad)"
            strokeWidth="6"
            fill="transparent"
            animate={{ pathLength: [0, 1] }}
            transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />

          <motion.circle
            cx="600"
            cy="200"
            r="12"
            fill="url(#aboutGrad)"
            animate={{ x: [0, 500, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.svg>
      </header>

      {/* ABOUT SECTION */}
      <section className="container mx-auto px-6 py-16 text-center relative z-10">
        <h2 className="text-3xl font-bold text-slate-800 mb-6">Who We Are</h2>
        <p className="max-w-3xl mx-auto text-slate-600 leading-relaxed">
          At ProcestaPH, we combine experience and compassion to offer personalized healthcare services. With a team of specialists and a patient-first philosophy, we ensure every treatment plan is rooted in trust, transparency, and care.
        </p>
      </section>

      {/* SERVICES SECTION */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-center text-slate-800 mb-12"
          >
            Our Services & Facilities
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((srv, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-gradient-to-br from-indigo-50 to-cyan-50 border border-indigo-100 rounded-2xl shadow-md p-6 hover:shadow-xl hover:-translate-y-2 transition-all"
              >
                <div className="text-4xl mb-4">{srv.icon}</div>
                <h3 className="text-lg font-semibold text-slate-700">{srv.title}</h3>
                <p className="text-slate-500 mt-2 text-sm leading-relaxed">{srv.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENTS SECTION */}
      <section className="py-20 bg-gradient-to-b from-cyan-50 to-white">
        <div className="container mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-slate-800 mb-12"
          >
            Conditions & Treatments We Provide
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {treatments.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                className="bg-white rounded-2xl shadow-md p-6 border border-cyan-100 hover:-translate-y-2 hover:shadow-xl transition-all"
              >
                <div className="text-4xl mb-3">{t.icon}</div>
                <h3 className="text-lg font-semibold text-slate-700">{t.name}</h3>
                <p className="text-slate-500 mt-2 text-sm leading-relaxed">{t.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER WAVE */}
      <motion.svg
        viewBox="0 0 1440 320"
        className="absolute bottom-0 left-0 w-full text-indigo-200"
        xmlns="http://www.w3.org/2000/svg"
		opacity="0.2"
      >
        <motion.path
          fill="currentColor"
          fillOpacity="1"
          d="M0,224L48,213.3C96,203,192,181,288,176C384,171,480,181,576,202.7C672,224,768,256,864,234.7C960,213,1056,139,1152,101.3C1248,64,1344,64,1392,64L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.svg>
    </div>
  );
}